import { toZonedTime } from 'date-fns-tz';

import { CostsData, EnergyUsageV2, PowerUsageV2, SavingsAPIData } from 'clipsal-cortex-types/src/api';
import { checkIfDatesAreOnSameDay, getCurrentDayInTimezone } from 'clipsal-cortex-utils/src/calculations/date-utils';
import { formatDate, parseDate } from 'clipsal-cortex-utils/src/formatting/formatting';

import costsMonthly from './stubs/time-series/costs-monthly.json';
import costs from './stubs/time-series/costs.json';
import energyUsageMonthly from './stubs/time-series/energy-usage-monthly.json';
import energyUsageWeekly from './stubs/time-series/energy-usage-weekly.json';
import energyUsageYearly from './stubs/time-series/energy-usage-yearly.json';
import energyUsage from './stubs/time-series/energy-usage.json';
import powerUsage from './stubs/time-series/power-usage.json';
import savingsMonthly from './stubs/time-series/savings-monthly.json';
import savings from './stubs/time-series/savings.json';

const DATA_MAP = {
  powerUsage: {} as Record<string, PowerUsageV2[]>,
  energyUsage: {} as Record<string, EnergyUsageV2[]>,
  energyUsageWeekly: {} as Record<string, EnergyUsageV2[]>,
  energyUsageMonthly: {} as Record<string, EnergyUsageV2[]>,
  costs: {} as Record<string, CostsData[]>,
  costsMonthly: {} as Record<string, CostsData>,
  savings: {} as Record<string, SavingsAPIData[]>,
  savingsMonthly: {} as Record<string, SavingsAPIData>,
};

export const DEMO_SITE_TIMEZONE = 'America/Los_Angeles';
export const CURRENT_DAY_IN_TIMEZONE = getCurrentDayInTimezone(DEMO_SITE_TIMEZONE);
export const TOTAL_NUM_DAYS = 30;

export function populateDailyDataMap() {
  const numDaysToMoveBack = TOTAL_NUM_DAYS - 1;
  const dateCursor = new Date(CURRENT_DAY_IN_TIMEZONE);

  try {
    // Traverse back 30 days and populate data into a map
    for (let i = 0; i <= numDaysToMoveBack; i++) {
      const powerUsageData = powerUsage[i] as PowerUsageV2[];
      const firstEntry = new Date(powerUsageData[0].reading_datetime);
      const dateFormatted = formatDate(dateCursor);
      firstEntry.setDate(dateCursor.getDate());
      firstEntry.setMonth(dateCursor.getMonth());
      firstEntry.setFullYear(dateCursor.getFullYear());

      DATA_MAP.powerUsage[dateFormatted] = powerUsageData.map((interval) => {
        const dateTime = firstEntry.toISOString();
        firstEntry.setMinutes(firstEntry.getMinutes() + 15);

        return {
          ...interval,
          reading_datetime: dateTime,
        };
      });

      DATA_MAP.costs[dateFormatted] = costs[i] as CostsData[];
      costs[i][0].date = dateFormatted;
      DATA_MAP.savings[dateFormatted] = savings[i];
      savings[i][0].date = dateFormatted;
      DATA_MAP.energyUsage[dateFormatted] = energyUsage[i];
      energyUsage[i][0].reading_date = dateFormatted;

      if (i % 7 === 0) {
        const weekNum = i / 7;
        // Get the date for the Monday of this week (the start date of the week)
        const dateForMonday = new Date(dateCursor);
        const weekdayNum = dateForMonday.getDay();
        dateForMonday.setDate(dateForMonday.getDate() - weekdayNum + 1);
        const dateForMondayFormatted = formatDate(dateForMonday);
        DATA_MAP.energyUsageWeekly[dateForMondayFormatted] = energyUsageWeekly[weekNum];
        energyUsageWeekly[weekNum][0].reading_date = dateForMondayFormatted;

        // We need to add an extra week on the back, in case we're past the previous week cut over
        if (weekNum === 3) {
          const dateWeekBefore = new Date(dateForMonday);
          dateWeekBefore.setDate(dateWeekBefore.getDate() - 7);
          DATA_MAP.energyUsageWeekly[formatDate(dateWeekBefore)] = energyUsageWeekly[weekNum + 1];
          energyUsageWeekly[weekNum + 1][0].reading_date = formatDate(dateWeekBefore);
        }
      }

      dateCursor.setDate(dateCursor.getDate() - 1);
    }

    // Iterate from the beginning of the year to December
    const monthDateCursor = new Date();
    monthDateCursor.setMonth(0);
    monthDateCursor.setDate(1);

    let monthIndex = 0;

    while (monthIndex < 12) {
      const firstDayOfMonthFormatted = formatDate(monthDateCursor);
      const energyUsageValueForMonth = energyUsageMonthly[monthIndex];
      energyUsageValueForMonth.reading_date = firstDayOfMonthFormatted;
      DATA_MAP.energyUsageMonthly[formatDate(monthDateCursor)] = [energyUsageValueForMonth];

      const savingsValueForMonth = savingsMonthly[monthIndex];
      savingsValueForMonth.date = firstDayOfMonthFormatted;
      DATA_MAP.savingsMonthly[formatDate(monthDateCursor)] = savingsValueForMonth;

      const costsValueForMonth = costsMonthly[monthIndex] as CostsData;
      costsValueForMonth.date = firstDayOfMonthFormatted;
      DATA_MAP.costsMonthly[formatDate(monthDateCursor)] = costsValueForMonth;

      monthDateCursor.setMonth(monthDateCursor.getMonth() + 1);
      monthIndex++;
    }
  } catch (e) {
    console.error(e);
  }
}

// Builds the data map once with appropriate dates from the last 30 days
populateDailyDataMap();

export function getSavingsData(queryParams: URLSearchParams) {
  const groupBy = queryParams.get('groupby') as string;
  const startDate = queryParams.get('start_date') as string;
  const endDate = queryParams.get('end_date') as string;

  if (!groupBy || groupBy === 'day') {
    if (startDate === endDate) {
      const data = DATA_MAP.savings[startDate];

      if (data) {
        return data;
      } else {
        // Some cases (e.g. week view moving back > 4 weeks) can cause fetches to savings out of the last 30-day range.
        // In that case, just get the savings for the current day.
        return DATA_MAP.savings[formatDate(CURRENT_DAY_IN_TIMEZONE)];
      }
    } else {
      const cursor = parseDate(startDate);
      const end = parseDate(endDate);
      const savingsData = [];

      while (cursor.getTime() < end.getTime()) {
        const dataForDay = DATA_MAP.savings[formatDate(cursor)];

        dataForDay?.[0] && savingsData.push(dataForDay[0]);
        cursor.setDate(cursor.getDate() + 1);
      }

      return savingsData;
    }
  }

  if (groupBy === 'month') {
    return Object.values(DATA_MAP.savingsMonthly);
  }

  return [];
}

export function getCostsData(queryParams: URLSearchParams) {
  const groupBy = queryParams.get('groupby');
  const startDate = queryParams.get('start_date') as string;
  const endDate = queryParams.get('end_date') as string;

  if (!groupBy || groupBy === 'day') {
    if (startDate === endDate) {
      const data = DATA_MAP.costs[startDate];

      if (data) {
        return data;
      } else {
        // Some cases (e.g. week view moving back > 4 weeks) can cause fetches to savings out of the last 30 day range.
        // In that case, just get the savings for the current day.
        return DATA_MAP.costs[formatDate(CURRENT_DAY_IN_TIMEZONE)];
      }
    } else {
      const cursor = parseDate(startDate);
      const end = parseDate(endDate);
      const costsData = [];

      while (cursor.getTime() < end.getTime()) {
        const dataForDay = DATA_MAP.costs[formatDate(cursor)];

        dataForDay?.[0] && costsData.push(dataForDay[0]);
        cursor.setDate(cursor.getDate() + 1);
      }

      return costsData;
    }
  }

  if (groupBy === 'month') {
    return Object.values(DATA_MAP.costsMonthly);
  }

  return [];
}

const EMPTY_POWER_USAGE_ENTRY = {
  reading_datetime: '',
  solar: null,
  grid: null,
  consumed: null,
  appliances: [
    {
      id: 2524,
      power: null,
    },
    {
      id: 2522,
      power: null,
    },
    {
      id: 2525,
      power: null,
    },
    {
      id: 2501,
      power: null,
    },
  ],
  battery_soc_fraction: null,
  battery: null,
};

export function getPowerUsageData(queryParams: URLSearchParams) {
  const fromDate = new Date(queryParams.get('from_datetime') as string);

  // Ensure data past the current time in the timezone is not plotted in the chart
  if (checkIfDatesAreOnSameDay(fromDate, CURRENT_DAY_IN_TIMEZONE)) {
    const currentTimeInTimezone = toZonedTime(new Date(), DEMO_SITE_TIMEZONE);

    return DATA_MAP.powerUsage[formatDate(fromDate)].map((interval) => {
      const readingDateTimeLocalized = toZonedTime(new Date(interval.reading_datetime), DEMO_SITE_TIMEZONE);
      if (readingDateTimeLocalized > currentTimeInTimezone) {
        return {
          ...EMPTY_POWER_USAGE_ENTRY,
          reading_datetime: interval.reading_datetime,
        };
      }

      return interval;
    });
  } else {
    return DATA_MAP.powerUsage[formatDate(fromDate)];
  }
}

export function getEnergyUsageData(queryParams: URLSearchParams) {
  const groupBy = queryParams.get('groupby');
  // Start date is provided for fetching a single day -- this is really confusing...
  const startDate = queryParams.get('start_date') as string;
  const endDate = queryParams.get('end_date') as string;
  const fromDate = queryParams.get('from_date') as string; // Deprecated
  const toDate = queryParams.get('to_date') as string; // Deprecated
  const periodType = getPeriodType(fromDate ?? startDate, toDate ?? endDate);

  if (!groupBy || groupBy === 'day') {
    if (fromDate && toDate) {
      const cursor = parseDate(fromDate);
      const end = parseDate(toDate);
      const energyUsageData = [];

      while (cursor.getTime() < end.getTime()) {
        const dataForDay = DATA_MAP.energyUsage[formatDate(cursor)];
        dataForDay?.[0] && energyUsageData.push(dataForDay[0]);
        cursor.setDate(cursor.getDate() + 1);
      }

      return energyUsageData;
    } else {
      // Params missing, so it's today
      return DATA_MAP.energyUsage[startDate];
    }
  }

  if (groupBy === 'week') {
    return DATA_MAP.energyUsageWeekly[fromDate];
  }

  if (groupBy === 'month') {
    if (periodType === 'YEAR') {
      return Object.entries(DATA_MAP.energyUsageMonthly).map(([, v]) => {
        const [value] = v;
        return value;
      });
    } else {
      return DATA_MAP.energyUsageMonthly[fromDate];
    }
  }

  if (groupBy === 'year') {
    return energyUsageYearly;
  }

  return [];
}

/**
 * Discerns the period type between two date or datetime strings, formatted like `YYYY-MM-DD [HH:MM:SS]`.
 *
 * @param start - The start date or datetime string.
 * @param end - The end date or datetime string.
 * @returns A string union of period types.
 */
function getPeriodType(start: string, end: string) {
  const startDate = parseDate(start);
  const endDate = parseDate(end);

  if (endDate.getTime() - startDate.getTime() === 1000 * 60 * 60 * 24 * 7) {
    return 'WEEK';
  }

  if (startDate.getDate() === 1 && endDate.getDate() === 1 && startDate.getMonth() === endDate.getMonth() - 1) {
    return 'MONTH';
  }

  if (startDate.getMonth() === 0 && endDate.getMonth() === 0) {
    return 'YEAR';
  }

  return 'DAY';
}
